// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {computeNativeParticipantVolume} from '@app/features/voice/engine/native_voice_engine/nativeVoiceVolume';
import type {VoiceEngine} from '@app/features/voice/engine/native_voice_engine/VoiceEngine';
import type {LivekitParticipantSnapshot} from '@app/features/voice/engine/VoiceParticipantManager';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {reaction} from 'mobx';

const logger = new Logger('NativeVoiceDeviceSync');

export {computeNativeParticipantVolume};

interface NativeVoiceDeviceSyncDeps {
	engine: VoiceEngine;
	getParticipants: () => Readonly<Record<string, LivekitParticipantSnapshot>>;
}

export function bindNativeVoiceDeviceSync(deps: NativeVoiceDeviceSyncDeps): () => void {
	const {engine, getParticipants} = deps;

	const applyOutputDevice = (deviceId: string): void => {
		if (!deviceId) return;
		void engine.setAudioOutputDevice(deviceId).catch((error) => {
			logger.warn('Native output-device sync failed', {deviceId, error});
		});
	};

	const applyVolumes = (): void => {
		const outputVolumePercent = VoiceSettings.getOutputVolume();
		for (const participant of Object.values(getParticipants())) {
			if (participant.isLocal) continue;
			if (!participant.sid || !participant.userId) continue;
			const volume = computeNativeParticipantVolume({
				userVolumePercent: ParticipantVolume.getVolume(participant.userId),
				outputVolumePercent,
				locallyMuted: ParticipantVolume.isLocalMuted(participant.userId),
			});
			void engine.setParticipantVolume(participant.sid, volume).catch((error) => {
				logger.warn('Native participant-volume sync failed', {sid: participant.sid, error});
			});
		}
	};

	applyOutputDevice(VoiceSettings.getOutputDeviceId());
	applyVolumes();

	const outputDeviceDisposer = reaction(
		() => VoiceSettings.getOutputDeviceId(),
		(deviceId) => applyOutputDevice(deviceId),
		{fireImmediately: false},
	);
	const volumeDisposer = reaction(
		() => ({
			volumes: ParticipantVolume.volumes,
			mutes: ParticipantVolume.localMutes,
			outputVolume: VoiceSettings.getOutputVolume(),
			participantSids: Object.values(getParticipants())
				.map((p) => p.sid)
				.join(','),
		}),
		() => applyVolumes(),
		{fireImmediately: false},
	);

	return () => {
		outputDeviceDisposer();
		volumeDisposer();
	};
}
